import { useTheme } from "@emotion/react";
import { Phone, Security, Refresh } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { useState, useEffect } from "react";
import { useMicrospecThemeLanguageContext } from "../Themes";

const LoginPhoneOtp = ({
  identifier,
  countryCode,
  otp,
  error,
  loading,
  resendLoading,
  redirectTo,
  onOtpChange,
  onSubmit,
  onResendOtp,
  onBackToIdentifier
}) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const theme = useTheme();
  const [countdown, setCountdown] = useState(0);

  // Generate gradients from theme colors
  const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

  // Format phone number for display
  const formatPhoneForDisplay = (phone, countryCode) => {
    if (!phone || !countryCode) return '';
    return `${countryCode} ${phone}`;
  };

  // Start countdown after OTP is sent
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendOtp = () => {
    onResendOtp();
    setCountdown(60); // Start 60 second countdown
  };

  const handleOtpInputChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and limit to 6 digits
    if (/^\d{0,6}$/.test(value)) {
      onOtpChange(value);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      {/* Disabled Phone Field */}
      <TextField
        margin="normal"
        fullWidth
        id="identifier"
        name="identifier"
        label={localizationProvider.translateByType({
          type: "Internal",
          text: "phoneNumber"
        })}
        type="tel"
        autoComplete="tel"
        dir="rtl"
        variant="outlined"
        value={formatPhoneForDisplay(identifier, countryCode)}
        disabled
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Phone sx={{ color: 'action.disabled' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          // '& .MuiOutlinedInput-root': {
          //   borderRadius: 3,
          //   backgroundColor: 'rgba(0,0,0,0.02)',
          //   '&.Mui-disabled': {
          //     '& fieldset': {
          //       borderColor: 'action.disabled',
          //     },
          //   },
          // },
        }}
      />

      {/* OTP Instructions */}
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          mt: 2,
          mb: 2,
          color: 'text.secondary',
          lineHeight: 1.6
        }}
      >
        {localizationProvider.translateByType({
          type: "Internal",
          text: "otpSentMessage"
        })}
      </Typography>

      {/* OTP Input */}
      <TextField
        margin="normal"
        required
        fullWidth
        id="otp"
        name="otp"
        label={localizationProvider.translateByType({
          type: "Internal",
          text: "verificationCode"
        })}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        dir="ltr"
        variant="outlined"
        value={otp}
        onChange={handleOtpInputChange}
        placeholder="123456"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Security sx={{ color: 'action.active' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            // backgroundColor: 'rgba(255,255,255,0.9)',
            transition: 'all 0.3s ease',
            letterSpacing: '0.5em',
            textAlign: 'center',
            fontSize: '1.2rem',
            '&:hover': {
              // backgroundColor: 'rgba(255,255,255,1)',
              '& fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
              },
            },
            '&.Mui-focused': {
              // backgroundColor: 'rgba(255,255,255,1)',
              boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
              '& fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
              },
            },
          },
          '& input': {
            textAlign: 'center',
            fontSize: '1.2rem',
            fontWeight: 600,
            letterSpacing: '0.3em',
          },
        }}
        inputProps={{
          minLength: 6,
          maxLength: 6,
        }}
      />

      {/* Resend OTP */}
      <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
        {countdown > 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {localizationProvider.translateByType({
              type: "Internal",
              text: "resendOtpIn"
            })}
          </Typography>
        ) : (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={handleResendOtp}
            disabled={resendLoading}
            sx={{
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              '&:hover': {
                textDecoration: 'underline',
              },
              '&:disabled': {
                color: 'action.disabled',
                cursor: 'not-allowed',
              },
            }}
          >
            <Refresh sx={{ fontSize: '1rem' }} />
            {resendLoading
              ? (localizationProvider.translateByType({
                type: "Internal",
                text: "sendingOtp"
              }))
              : (localizationProvider.translateByType({
                type: "Internal",
                text: "resendOtp"
              }))
            }
          </Link>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Verify Button */}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading || otp.length !== 6}
        sx={{
          mt: 2,
          mb: 2,
          py: 1.8,
          borderRadius: 3,
          fontSize: '1rem',
          fontWeight: 700,
          textTransform: 'none',
          background: primaryGradient,
          boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 40px ${theme.palette.primary.main}40`,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&:disabled': {
            background: 'action.disabledBackground',
            color: 'action.disabled',
            transform: 'none',
            boxShadow: 'none',
          },
        }}
      >
        {loading
          ? (localizationProvider.translateByType({
            type: "Internal",
            text: "verifying"
          }) || "جاري التحقق...")
          : (localizationProvider.translateByType({
            type: "Internal",
            text: "verifyAndSignIn"
          }) || "تحقق وتسجيل الدخول")
        }
      </Button>

      {/* Back to Identifier Link */}
      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onBackToIdentifier}
          sx={{
            color: theme.palette.primary.main,
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {localizationProvider.translateByType({
            type: "Internal",
            text: "backToPhoneSelection"
          }) || "العودة لاختيار رقم الهاتف"}
        </Link>
      </Box>
    </Box>
  );
};

export default LoginPhoneOtp;